import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import detachedHouse from '@propertypal/shared/src/resources/icons/filters/detached-icon.svg?url';
import panoramicIcon from '@propertypal/shared/src/resources/icons/property/panoramic-icon.svg?url';
import { Property } from '@propertypal/shared/src/types/property';
import { GalleryItem } from '@propertypal/shared/src/utils/property/getGalleryImages';
import trackEvents from '@propertypal/shared/src/utils/trackEvents';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, { FunctionComponent, ReactElement, ReactNode, useEffect } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import ImageSlider from '../image/ImageSlider';
import Magnifier from '../magnifier/Magnifier';
import { Text } from '../typography';
import PanZoomImage, { DEFAULT_ZOOM_POSITION, UsePanZoomImageData, usePanZoomImage } from '../zoom/PanZoomImage';
import {
  ArrowButton,
  Container,
  CounterContainer,
  Curtain,
  ImageBox,
  ImageOverlay,
  NoImageBackground,
  PanoramicIconBox,
  PhotoCounter,
  ZoomContainer,
  ZoomControl,
  ZoomControls,
} from './PropertySlider.style';
import TourIcon from './TourIcon';

export interface PropertySliderProps {
  alt: string;
  images: GalleryItem[];
  onClick?: (item: GalleryItem) => void;
  onCounterClick?: (item: GalleryItem) => void;
  imageIndex: number;
  setImageIndex: (index: number) => void;
  centerImageCounter?: boolean;
  renderImageOverlay?: () => ReactElement;
  fullscreen?: boolean;
  disableEvents?: boolean;
  children?: ReactNode;
  sitemap?: boolean;
  altCounter?: boolean;
  slidesPerView?: 1 | 'auto';
  maxHeight: string;
  hideCounter?: boolean;
  scrollTop?: number;
  property?: Property;
  panAndZoom?: boolean;
  showZoomLevel?: boolean;
}

const MAX_RATIO = 1.65;
const MIN_RATIO = 1;

const calculateRatio = (images: GalleryItem[]) => {
  let ratio: number = 0;

  images.forEach((image) => {
    const iRatio = image.width / image.height;

    if (ratio === 0 || iRatio > ratio) {
      ratio = Math.min(MAX_RATIO, iRatio);
    }
  });

  return Math.max(MIN_RATIO, ratio);
};

const normalizeIndex = (index: number, length: number) => {
  if (length === 0) return 0;

  return ((index % length) + length) % length;
};

interface PropertySliderCounterProps
  extends Pick<PropertySliderProps, 'centerImageCounter' | 'onCounterClick' | 'altCounter' | 'imageIndex' | 'images'> {}

export const PropertySliderCounter: React.FC<PropertySliderCounterProps> = (props) => {
  const theme = useTheme();

  const handleCounterClick = () => {
    if (props.onCounterClick && props.images[props.imageIndex]) {
      props.onCounterClick(props.images[props.imageIndex]);
    }
  };

  return (
    <CounterContainer center={props.centerImageCounter} data-testid="property-slider-counter">
      <PhotoCounter
        aria-label="Open photos dialog"
        onClick={handleCounterClick}
        as={props.onCounterClick ? 'button' : 'div'}
        $alt={props.altCounter}
        data-testid="photoCounter"
      >
        {!props.altCounter && (
          <FontAwesomeIcon
            icon={faCamera}
            color={theme.white}
            style={{ fontSize: props.altCounter ? 15 : 12, marginTop: 2, marginRight: 4 }}
          />
        )}
        <Text
          fontSize={props.altCounter ? 15 : 12}
          color={theme.white}
          fontWeight={props.altCounter ? 'bold' : undefined}
        >
          {props.imageIndex + 1}
          {props.altCounter ? ' of ' : '/'}
          {props.images.length}
        </Text>
      </PhotoCounter>
    </CounterContainer>
  );
};

const FloorPlanZoomControls: FunctionComponent<UsePanZoomImageData> = (props) => {
  return (
    <ZoomContainer>
      <ZoomControls>
        <ZoomControl
          icon={faMinus}
          onClick={() => {
            props.zoomOut();
          }}
          aria-disabled={props.position.z === 1}
          $isDisabled={props.position.z === 1}
          data-testid="zoom-out"
        />
        <p data-testid="zoom-amount">{props.zoomPercentage}%</p>
        <ZoomControl
          icon={faPlus}
          onClick={() => {
            props.zoomIn();
          }}
          aria-disabled={props.position.z === 2}
          $isDisabled={props.position.z === 2}
          data-testid="zoom-in"
        />
      </ZoomControls>
    </ZoomContainer>
  );
};

const PropertySlider: FunctionComponent<PropertySliderProps> = (props) => {
  const theme = useTheme();
  const touchDevice = useTouchDevice();
  const aspectRatio = calculateRatio(props.images || []);
  const zoomImage = usePanZoomImage();
  const images = props.images || [];
  const activeIndex = normalizeIndex(props.imageIndex ?? 0, Math.max(images.length, 1));
  const canDrag = !((props.sitemap && touchDevice) || (props.panAndZoom && zoomImage.position.z !== 1));

  useEffect(() => {
    // reset zoom position on index change
    if (activeIndex != null && props.panAndZoom) zoomImage.setPosition(DEFAULT_ZOOM_POSITION);
  }, [activeIndex, props.panAndZoom]);

  useEffect(() => {
    if (images[activeIndex]?.imageId && props.property) {
      trackEvents({
        eventType: 'SLIDESHOW_VIEW',
        objectId: images[activeIndex].imageId,
        property: props.property.type === 'property' ? props.property.id : undefined,
        development: props.property.type === 'development' ? props.property.id : undefined,
        eventPageType: props.property.type === 'development' ? 'DEVELOPMENT_DETAILS' : 'PROPERTY_DETAILS',
        parameters: {
          imageType: images[activeIndex].type,
        },
      });
    }
  }, [activeIndex, images, props.property]);

  const handleImageClick = (image: GalleryItem) => {
    if (image.index === activeIndex) {
      props.onClick?.(image);
      return;
    }

    props.setImageIndex(normalizeIndex(image.index, images.length));
  };

  if (images.length === 0) {
    return (
      <Container role="region" aria-label="property images" tabIndex={0}>
        <NoImageBackground data-testid="no-image">
          {props.children}
          {props.renderImageOverlay && props.renderImageOverlay()}

          <img src={detachedHouse.src} width={detachedHouse.width} height={detachedHouse.height} alt="house" />
          <Text>No photo available</Text>
        </NoImageBackground>
      </Container>
    );
  }

  return (
    <Container style={{ aspectRatio, height: '100%', maxHeight: props.maxHeight }}>
      {images.length > 0 && !props.hideCounter && (
        <PropertySliderCounter
          imageIndex={activeIndex}
          images={images}
          altCounter={props.altCounter}
          centerImageCounter={props.centerImageCounter}
          onCounterClick={props.onCounterClick}
        />
      )}

      {props.images && props.showZoomLevel && <FloorPlanZoomControls {...zoomImage} />}

      {/* Blocks ability on iOS to swipe history back and swipe the slider at the same time */}
      {/* Doing both at the same time creates a race condition between history back and history replace */}
      <Curtain style={{ left: 0 }} />
      <Curtain style={{ right: 0 }} />

      {props.children}

      <ImageSlider
        items={images}
        activeIndex={activeIndex}
        setActiveIndex={props.setImageIndex}
        slidesPerView={props.slidesPerView}
        disableEvents={props.disableEvents}
        canDrag={canDrag}
        getItemKey={(image) => image.key}
        getItemAspectRatio={(image) => image.width / image.height}
        renderNavigation={({ goPrev, goNext }) => {
          if (!(props.sitemap || !touchDevice) || images.length <= 1) {
            return null;
          }

          return (
            <>
              <ArrowButton
                aria-label="Previous image"
                side="left"
                onClick={goPrev}
                data-testid="slider-left"
                sitemapTouch={touchDevice && props.sitemap}
              >
                <FontAwesomeIcon icon={faAngleLeft} color={theme.white} style={{ fontSize: 60 }} />
              </ArrowButton>

              <ArrowButton
                aria-label="Next image"
                side="right"
                onClick={goNext}
                data-testid="slider-right"
                sitemapTouch={touchDevice && props.sitemap}
              >
                <FontAwesomeIcon icon={faAngleRight} color={theme.white} style={{ fontSize: 60 }} />
              </ArrowButton>
            </>
          );
        }}
        renderSlide={({ item: image, index, renderedIndex, isActive, isVisible, isMiddleCopy }) => {
          const imgAspectRatio = image.width / image.height;
          const isPanoramic = imgAspectRatio > MAX_RATIO;
          const className: string = props.fullscreen ? 'full-screen-img' : 'standard-img';
          const shouldRenderPanZoom = isVisible && props.panAndZoom && (images.length === 1 || isMiddleCopy);

          return (
            <ImageBox
              onClick={() => handleImageClick(image)}
              data-testid={isMiddleCopy || images.length === 1 ? `slide-${index + 1}` : undefined}
              panAndZoom={isActive && props.panAndZoom}
              $mode={props.slidesPerView === 1 ? 'single' : 'auto'}
            >
              {props.sitemap ? (
                isVisible ? (
                  <Magnifier
                    id={`property-slider-image-${index}-${renderedIndex}`}
                    src={image.image}
                    alt={`${props.alt} ${index + 1} of ${images.length}`}
                    width={image.width}
                    height={image.height}
                    className={className}
                  />
                ) : (
                  <div />
                )
              ) : shouldRenderPanZoom ? (
                <PanZoomImage
                  id={`property-slider-image-${index}-${renderedIndex}`}
                  src={image.image}
                  alt={`${props.alt} ${index + 1} of ${images.length}`}
                  interactive={isActive}
                  position={isActive ? zoomImage.position : DEFAULT_ZOOM_POSITION}
                  setPosition={zoomImage.setPosition}
                  touchDistanceRef={zoomImage.touchDistanceRef}
                  positionRef={zoomImage.positionRef}
                  width={image.width}
                  height={image.height}
                  className={className}
                  loading="eager"
                  draggable={false}
                />
              ) : isVisible ? (
                <img
                  id={`property-slider-image-${index}-${renderedIndex}`}
                  src={image.image}
                  alt={`${props.alt} ${index + 1} of ${images.length}`}
                  loading={index === activeIndex ? 'eager' : 'lazy'}
                  width={image.width}
                  height={image.height}
                  className={className}
                  draggable={false}
                />
              ) : (
                <div />
              )}

              {props.renderImageOverlay && props.renderImageOverlay()}

              {isPanoramic && !props.fullscreen && (
                <PanoramicIconBox>
                  <img
                    src={panoramicIcon.src}
                    width={panoramicIcon.width}
                    height={panoramicIcon.height}
                    alt="panoramic icon"
                  />
                </PanoramicIconBox>
              )}

              {(image.type === 'webview' || image.type === 'video') && (
                <ImageOverlay>
                  <TourIcon image={image} />

                  <Text>{image.text}</Text>
                </ImageOverlay>
              )}
            </ImageBox>
          );
        }}
      />
    </Container>
  );
};

export default PropertySlider;
